<template>
  <div class="sponsorship-form-new">
    <form @submit.prevent="submit">
      <magic-container>
        <v-text-field
          v-model="name"
          label="Logo Sponsorship title"
          required
          type="text"
          :error-messages="nameErrors"
          @blur="v$.name.$touch()"
        />

        <platform-select
          :is-multiple="false"
          :error-messages="countryErrors"
          v-model="country"
        />
      </magic-container>

      <layout-section-header
        title="Business details"
        :has-trigger="false"
      />

      <magic-container>
        <div class="d-flex flex-column">
          <v-autocomplete
            v-model="businessId"
            :items="businesses"
            label="Business"
            required
            clearable
            item-title="name"
            item-value="id"
            prepend-inner-icon="store"
            :hide-details="false"
            :loading="isLoadingBusinesses"
            v-model:search="businessSearch"
            :error-messages="businessIdErrors"
            @focus="onBusinessFocus"
            @update:model-value="isSelectingBusiness = true"
          />
          <dropdowns-merchant
            v-model="merchant"
            :hide-details="false"
            :countries="[country]"
            :error-messages="merchantErrors"
            is-single-country
            @focus="v$.country.$touch()"
          />
        </div>

        <div>
          <FileUploadArea
            ref="uploadArea"
            :on-upload-file="onUploadFile"
            :step-one-text="$t('submit_a_ticket_requesting')"
            :link-text="$t('link')"
            :step-one-link="fileUploadLink"
            :step-one-button-text="$t('i_have_the_logo')"
            step-two-text="Add or drag logo"
          />
        </div>
      </magic-container>

      <div>
        <alerts-base
          v-if="isTryingSubmit && !isImageSelected && !isDefaultMerchantImageSet"
          type="error"
          class="mb-2"
        >
          <template #text>
            Logo image is required
          </template>
        </alerts-base>
      </div>

      <layout-section-header
        title="Logo Sponsorship Details"
        :has-trigger="false"
      />

      <magic-container>
        <base-date-picker
          :date="dates"
          :config="dateConfig"
          :on-change="onChangeDate"
          :hide-details="false"
          :error-messages="datesErrors"
          placeholder="Start date and End date"
        />
        <div class="time-fields">
          <v-select
            v-model="startTime"
            :items="hours"
            :hide-details="false"
            :label="$t('start_time')"
            :error-messages="startTimeErrors"
            :on-blur="v$.startTime.$touch"
            required
          />
          <v-select
            v-model="endTime"
            :items="hours"
            :label="$t('end_time')"
            :hide-details="false"
          />
        </div>
      </magic-container>

      <layout-section-header
        title="Category Page"
        :has-trigger="false"
      />

      <div>
        <!-- todo unsure about custom-filter prop, maybe should be filter? -->
        <v-autocomplete
          clearable
          v-model="selectedCategories"
          :items="categories"
          prepend-inner-icon="category"
          :hide-details="false"
          :loading="isLoadingCategory"
          multiple
          chips
          closable-chips
          :return-object="true"
          :error-messages="selectedCategoriesErrors"
          @update:search="categorySearch"
          @focus="v$.country.$touch()"
          @update:model-value="onSelectCategory()"
          v-model:search="categorySearchInput"
          item-title="name"
          label="Category"
        >
          <template #chip="{ item, props }">
            <v-chip
              v-bind="props"
              closable
              close-icon="close"
              :color="getCategoryTextColor(item.raw)"
              @click:close="remove(item.raw)"
            >
              {{ item.raw.name }}
            </v-chip>
          </template>
        </v-autocomplete>
      </div>

      <div>
        <alerts-base
          v-for="error in categoriesErrors"
          :key="error.id"
          type="error"
          class="mb-5"
        >
          <template #text>
            The '{{ error.name }}' category is not available for the selected merchant on the following
            {{ error.unavailable_dates.length === 1 ? 'date' : 'dates' }}:
            {{ error.unavailable_dates.join('; ') }}{{ error.unavailable_dates_extra_count ? ', and more...' : '.' }}
          </template>
        </alerts-base>
      </div>

      <div class="form-actions">
        <v-btn
          variant="text"
          class="trigger"
          @click="cancel"
        >
          {{ $t('cancel') }}
        </v-btn>

        <v-btn
          color="primary"
          class="trigger"
          variant="flat"
          :loading="isLoading"
          @click="submit"
        >
          Submit Logo Sponsorship
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { debounce, isNonEmptyArr, isNonEmptyStr, isNum, isObj, isStr } from '@/utils';
  import { v1 as uuidv1 } from 'uuid';
  import FileUploadArea from '@/components/forms/sponsorships/FileUploadArea.vue';

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const categorySearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  // TODO: Translate component

  export default {
    setup() {
      return { v$: useVuelidate() }
    },
    components: {
      FileUploadArea,
    },
    validations() {
      return {
        businessId: {
          required,
        },
        country: {
          required,
        },
        name: {
          required,
        },
        merchant: {
          required,
        },
        selectedCategories: {
          required,
        },
        dates: {
          required,
        },
        startTime: {
          required,
        },
        endTime: {
          required,
        },
      };
    },
    data() {
      return {
        businessId: null,
        businessSearch: '',
        isSelectingBusiness: false,
        isLoadingBusinesses: false,
        selectedCategories: [],
        categorySearchInput: '',
        unavailableCategories: [],
        country: null,
        merchant: null,
        dates: '',
        startDate: '',
        startTime: this.getStartTime(),
        endTime: this.$dayjs().endOf('day').minute(45).format('HH:mm'),
        hours: this.getHours(),
        endDate: '',
        dateConfig: {
          minDate: 'today',
          mode: 'range',
        },
        name: '',
        isLoadingCategory: false,
        isAddingSponsor: false,
        isTryingSubmit: false,
        // todo unsure maybe image: undefined
        image: null,
        isDefaultMerchantImageSet: false,
        fileUploadLink: import.meta.env.VUE_APP_HELP_SPONSORSHIPS_URL
      };
    },
    computed: {
      categories: {
        get() {
          const storeCategories = this.$store.getters['pepper/groups'] ?? [];
          const missing = this.selectedCategories.filter(
            cat => storeCategories.findIndex(c => c.id === cat.id) === -1
          );
          return missing.length ? [...storeCategories, ...missing] : storeCategories;
        },
        set(newCategories) {
          return newCategories;
        },
      },
      countryErrors() {
        if (
          !this.v$.selectedCategories.$dirty &&
          !this.v$.merchant.$dirty &&
          this.v$.country.$dirty &&
          !this.country
        ) {
          return [this.$t('please_select_platform_first')];
        }
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('country_required_error');
          }
          return error.$message;
        });
      },
      isLoading() {
        return this.isAddingSponsor;
      },
      businesses() {
        return this.$store.getters['businesses/items'];
      },
      businessIdErrors() {
        return this.v$.businessId.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Business is required';
          }
          return error.$message;
        });
      },
      datesErrors() {
        return this.v$.dates.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Start and end dates are required';
          }
          return error.$message;
        });
      },
      nameErrors() {
        return this.v$.name.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Name is required';
          }
          return error.$message;
        });
      },
      merchantErrors() {
        return this.v$.merchant.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Merchant is required';
          }
          return error.$message;
        });
      },
      selectedCategoriesErrors() {
        return this.v$.selectedCategories.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Category is required';
          }
          return error.$message;
        });
      },
      startTimeErrors() {
        return this.v$.startTime.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Start time is required';
          }
          return error.$message;
        });
      },
      isImageSelected() {
        // TODO unsure undefined or null
        return this.image !== undefined;
      },
      categoriesErrors() {
        const errors = [];

        this.selectedCategories.forEach(({id, name}) => {
          this.unavailableCategories.findIndex(({external_id, unavailable_dates, unavailable_dates_extra_count}) => {
            if (external_id === id) {
              errors.push({
                unavailable_dates: unavailable_dates.split(',').map(date => this.$dayjs(date).format('MMM D, YYYY')),
                unavailable_dates_extra_count,
                name,
              })
              return true;
            }
            return false;
          })
        });

        return errors;
      }
    },
    watch: {
      country(newVal, oldVal) {
        if (isNonEmptyStr(newVal) && newVal !== oldVal) {
          this.selectedCategories = [];
          // here we refresh only categories
          this.getCategory();
          this.getUsedCategories();
          // merchants are refreshed automatically inside <MerchantDropdown/>

          if (this.isDefaultMerchantImageSet) {
            this.clearDefaultMerchantImage();
          }
        }
      },
      businessSearch(nextSearch) {
        if (this.isSelectingBusiness) {
          this.isSelectingBusiness = false;
          return;
        }
        if (isStr(nextSearch)) {
          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      merchant(newVal) {
        if (isObj(newVal) && isNonEmptyStr(newVal.sponsorship_logo_url)) {
          this.$refs.uploadArea.setImage(newVal.sponsorship_logo_url);
          this.isDefaultMerchantImageSet = true;
        } else {
          if (this.isDefaultMerchantImageSet) {
            this.clearDefaultMerchantImage();
          }

          this.isDefaultMerchantImageSet = false;
        }

        this.getUsedCategories();
      },
      dates() {
        this.getUsedCategories();
      },
      // todo unsure if we need selectedCategories
      selectedCategories(arr) {
        this.hasCategoriesError = -1 !== arr.findIndex(cat =>
          -1 !== this.unavailableCategories.findIndex(unCat => unCat.external_id === cat.id)
        );
      },
    },
    methods: {
      onSelectCategory() {
        this.categorySearchInput = '';
      },
      clearDefaultMerchantImage() {
        this.$refs.uploadArea.step = 2;
        this.image = null;
      },
      getCategoryTextColor(item) {
        if (this.unavailableCategories.findIndex(cat => cat.external_id === item.id) !== -1) {
          return '#D32F2F';
        }
        return '#000';
      },
      remove (item) {
        const index = this.selectedCategories.findIndex(cat => {
          return cat.id === item.id;
        })
        if (index >= 0) this.selectedCategories.splice(index, 1)
      },
      threadSearch(nextVal) {
        if (isStr(nextVal) && nextVal !== '') {
          if (this.isRefreshingThreads) {
            this.isRefreshingThreads = false;
            return false;
          }

          threadSearchDebouncer(() => this.getThread(nextVal));
        }
      },
      categorySearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingCategory) {
            this.isRefreshingCategory = false;
            return false;
          }

          // todo unsure if we need this if condition
          if (isNonEmptyStr(nextSearch)) {
            categorySearchDebouncer(() => this.getCategory(nextSearch));
          }
        }
      },
      getUsedCategories() {
        if (
          !isNonEmptyStr(this.startDate) ||
          !isNonEmptyStr(this.startTime) ||
          !isNonEmptyStr(this.endDate) ||
          !isNonEmptyStr(this.endTime) ||
          !isNum(this.merchant?.id) ||
          !isNonEmptyStr(this.country)
        ) {
          return;
        }

        const query = {
          start_date: this.startDate + ' ' + this.startTime,
          end_date: this.endDate + ' ' + this.endTime,
          merchant_id: this.merchant.id,
          country: this.country,
        };

        return this.$store.dispatch('sponsorships/getUsedCategories', query).then(res => {
          this.unavailableCategories = res;
          return res;
        })
      },
      getCategory(search) {
        if (!isNonEmptyStr(this.country)) {
          this.v$.country.$touch();
          return;
        }
        const query = {
          countries: [this.country],
          search,
        };

        this.isLoadingCategory = true;

        return this.$store
          .dispatch('pepper/getGroups', query)
          .then(({ groups }) => {
            this.categories = groups;
            this.isLoadingCategory = false;
            return groups;
          })
          .catch((err) => {
            this.isLoadingCategory = false;
            return err;
          });
      },
      getMerchants(search = '', country) {
        const query = {
          search,
        };

        if (isNonEmptyStr(country)) {
          query.countries = [country];
        } else {
          this.v$.country.$touch();
          return;
        }

        return this.$store.dispatch('pepper/getMerchants', query);
      },
      onBusinessFocus() {
        if (!isNonEmptyArr(this.businesses)) {
          this.getBusinesses();
        }
      },
      getBusinesses(search = '') {
        const query = {
          status: 1, // only active businesses
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItems', query)
          .then((res) => {
            this.isLoadingBusinesses = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      onUploadFile(file) {
        this.isDefaultMerchantImageSet = false;
        this.image = file.file;
      },
      onChangeDate(dates) {
        this.dates = dates;
        const [startDate, endDate] = dates.split(' - ');
        this.startDate = startDate;
        this.endDate = endDate ? endDate : startDate;// if user selects only one date
      },
      cancel() {
        this.clear();
        this.$router.push(`/sponsorships`);
      },
      submit() {
        this.v$.$touch();
        this.isTryingSubmit = true;

        if (!this.canSubmitRequest() || this.v$.$invalid) {
          return false;
        }

        this.addSponsor();
      },
      clear() {
        this.v$.$reset();
        this.name = '';
        this.merchant = null;
        this.isAddingSponsor = false;
        this.businessSearch = '';
        this.$store.dispatch('businesses/clearItems');
        this.$store.dispatch('pepper/clearMerchants');
        this.$store.dispatch('pepper/clearGroups');
      },
      addSponsor() {
        const {
          name, merchant, country, businessId,
          selectedCategories, image, startTime, endTime
        } = this;

        const sponsorship = {
          name,
          merchant: {
            id: merchant.id,
            // sponsorship_logo_url: merchant.sponsorship_logo_url, // not sure if this is needed
          },
          country,
          start_date: this.startDate + ' ' + startTime,
          end_date: this.endDate + ' ' + endTime,
          business_id: businessId,
          categories: selectedCategories,
          image,
        };

        this.isAddingSponsor = true;

        return this.$store.dispatch('sponsorships/createSponsorship', sponsorship)
          .then(res => {
            this.clear();
            this.$router.push({ name: 'logo-sponsorships', query: { status: 'success' } });

            return res;
          })
          .finally(() => {
            this.isAddingSponsor = false;
          });
      },
      canSubmitRequest() {
        const {
          name, merchant, isAddingSponsor, country, businessId,
          selectedCategories, dates, isImageSelected, categoriesErrors,
          isDefaultMerchantImageSet
        } = this;

        const hasRequiredFields =
          isNonEmptyStr(name) &&
          isNonEmptyStr(country) &&
          isNonEmptyStr(dates) &&
          isNum(businessId) &&
          isNum(merchant.id) &&
          isNonEmptyArr(selectedCategories) &&
          !categoriesErrors.length &&
          (isImageSelected || isDefaultMerchantImageSet);

        return hasRequiredFields && !isAddingSponsor;
      },
      getHours() {
        const { $dayjs } = this;
        const items = [];
        const hours = new Array(24).fill();
        const minutesParts = new Array(3).fill();
        const minutesStep = 15;

        hours.forEach((acc, index) => {
          let minutes = 15;
          items.push($dayjs().hour(index).minute(0).format('HH:mm'));

          minutesParts.forEach(() => {
            items.push($dayjs().hour(index).minute(minutes).format('HH:mm'));
            minutes += minutesStep;
          });
        });

        return items;
      },
      getStartTime() {
        return this.$dayjs()
          .add(30 - (this.$dayjs().minute() % 30), 'minutes')
          .format('HH:mm');
      },
    },
  };
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

.sponsorship-form-new {
  padding: $spacing-xl $spacing-md;
  background-color: rgb(var(--v-theme-surface));

  .time-fields {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .v-btn {
      margin: 0 $spacing-sm 0 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
